/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ensureAuthenticated } from '../utilities/is-authenticated';
import { trackedAction } from '../utilities/track-execution-time';
import { IPostCreateOrderData } from './create-order/post-create-order.action';

/**
 * PostTransferCartPunchout Input Action
 */
export class PostTransferCartPunchoutInput implements Msdyn365.IActionInput {
    public orderData: IPostCreateOrderData;
    public buyerCookie: string;
    public cartTransferUrl: string;
    public returnUrl: string;
    constructor(orderData: IPostCreateOrderData, buyerCookie: string, cartTransferUrl: string, returnUrl: string) {
        this.orderData = orderData;
        this.buyerCookie = buyerCookie;
        this.cartTransferUrl = cartTransferUrl;
        this.returnUrl = returnUrl;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `PostTransferCartPunchout`;
    public getCacheObjectType = () => 'PostTransferCartPunchout';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IPostTransferCartPunchoutResponseData {
    success: boolean;
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostTransferCartPunchoutInput({} as IPostCreateOrderData, '', '', '');
};

async function action(input: PostTransferCartPunchoutInput, ctx: Msdyn365.IActionContext): Promise<IPostTransferCartPunchoutResponseData> {
    ensureAuthenticated(ctx);

    if (!input.cartTransferUrl) {
        throw new Error('No cart transfer URL found in session storage');
    }
    let transferSuccess = false;
    await Msdyn365.sendRequest(input.cartTransferUrl, 'post', {
        buyerCookie: input.buyerCookie,
        returnUrl: input.returnUrl,
        order: input.orderData
    })
        .then(() => {
            transferSuccess = true;
        })
        .catch(error => {
            console.error('Error transferring cart', error);
            throw new Error('Error transferring cart');
        });
    return { success: transferSuccess };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostTransferCartPunchoutResponseData>>(
        ((input, ctx) => trackedAction(action, input, ctx, 'TransferCartPunchout'))
    ),
    id: 'PostTransferCartPunchout',
    input: createInput
});
